import { supabase } from '@/lib/supabase';
import { restHeaders } from '@/lib/supabaseRest';

export type ActivityType =
  | 'property_created'
  | 'property_updated'
  | 'property_status_changed'
  | 'deal_created'
  | 'deal_updated'
  | 'deal_status_changed'
  | 'checklist_updated'
  | 'task_created'
  | 'task_completed'
  | 'task_updated'
  | 'offer_created'
  | 'offer_updated'
  | 'offer_status_changed'
  | 'contact_created'
  | 'contact_updated'
  | 'document_uploaded'
  | 'note_added'
  | 'financing_created'
  | 'financing_updated'
  | 'om_uploaded'
  | 'om_status_changed'
  | 'inbound_lead_received';

export type EntityType =
  | 'property'
  | 'deal'
  | 'task'
  | 'offer'
  | 'contact'
  | 'document'
  | 'checklist'
  | 'financing'
  | 'offering_memorandum'
  | 'inbound_lead';

export interface ActivityLog {
  id: string;
  activity_type: ActivityType;
  entity_type: EntityType;
  entity_id: string;
  entity_name: string;
  property_id: string | null;
  property_name: string | null;
  deal_id: string | null;
  deal_name: string | null;
  description: string;
  details: Record<string, unknown> | null;
  user_id: string;
  user_name: string;
  user_email: string;
  created_at: string;
}

export interface ActivityFilters {
  property_id?: string;
  deal_id?: string;
  entity_type?: EntityType;
  activity_type?: ActivityType;
  user_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface ActivityListResponse {
  data: ActivityLog[];
  count: number;
}

/**
 * Fetch activity logs with optional filters and pagination
 */
export async function getActivityLogs(
  filters?: ActivityFilters,
  page = 1,
  pageSize = 50
): Promise<ActivityListResponse> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    // Build query parameters
    const params = new URLSearchParams();
    params.append('select', '*');
    params.append('order', 'created_at.desc');

    // Apply filters
    if (filters?.property_id) {
      params.append('property_id', `eq.${filters.property_id}`);
    }
    if (filters?.deal_id) {
      params.append('deal_id', `eq.${filters.deal_id}`);
    }
    if (filters?.entity_type) {
      params.append('entity_type', `eq.${filters.entity_type}`);
    }
    if (filters?.activity_type) {
      params.append('activity_type', `eq.${filters.activity_type}`);
    }
    if (filters?.user_id) {
      params.append('user_id', `eq.${filters.user_id}`);
    }
    if (filters?.from_date) {
      params.append('created_at', `gte.${filters.from_date}`);
    }
    if (filters?.to_date) {
      params.append('created_at', `lte.${filters.to_date}`);
    }

    // Pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const url = `${supabaseUrl}/rest/v1/activity_logs?${params.toString()}`;

    const response = await fetch(url, {
      headers: await restHeaders({
        'Range': `${from}-${to}`,
        'Prefer': 'count=exact'
      })
    });

    if (!response.ok) {
      // If table doesn't exist yet, return empty result
      if (response.status === 404 || response.status === 400) {
        console.warn('Activity logs table may not exist yet');
        return { data: [], count: 0 };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const contentRange = response.headers.get('content-range');
    const count = contentRange ? parseInt(contentRange.split('/')[1]) : data.length;

    return {
      data: data || [],
      count
    };
  } catch (error) {
    console.error('Error in getActivityLogs:', error);
    // Return empty result instead of throwing for graceful degradation
    return { data: [], count: 0 };
  }
}

/**
 * Log an activity - this will be called from other services when actions occur
 */
export async function logActivity(activity: Omit<ActivityLog, 'id' | 'created_at'>): Promise<ActivityLog | null> {
  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .insert(activity)
      .select()
      .single();

    if (error) {
      console.error('Error logging activity:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in logActivity:', error);
    return null;
  }
}

/**
 * Get activity summary stats
 */
export async function getActivityStats(): Promise<{
  today: number;
  thisWeek: number;
  thisMonth: number;
}> {
  try {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const [todayResult, weekResult, monthResult] = await Promise.all([
      getActivityLogs({ from_date: todayStart }, 1, 1),
      getActivityLogs({ from_date: weekStart }, 1, 1),
      getActivityLogs({ from_date: monthStart }, 1, 1),
    ]);

    return {
      today: todayResult.count,
      thisWeek: weekResult.count,
      thisMonth: monthResult.count,
    };
  } catch (error) {
    console.error('Error getting activity stats:', error);
    return { today: 0, thisWeek: 0, thisMonth: 0 };
  }
}

/**
 * Generate activity description based on type
 */
export function generateActivityDescription(
  activityType: ActivityType,
  entityName: string,
  details?: Record<string, unknown>
): string {
  switch (activityType) {
    case 'property_created':
      return `created property "${entityName}"`;
    case 'property_updated':
      return `updated property "${entityName}"`;
    case 'property_status_changed':
      return `changed status of "${entityName}" to ${details?.new_status || 'unknown'}`;
    case 'deal_created':
      return `created deal "${entityName}"`;
    case 'deal_updated':
      return `updated deal "${entityName}"`;
    case 'deal_status_changed':
      return `moved deal "${entityName}" to ${details?.new_stage || details?.new_status || 'unknown'}`;
    case 'checklist_updated':
      return `updated checklist for "${entityName}"${details?.field_name ? ` (${details.field_name})` : ''}`;
    case 'task_created':
      return `created task "${entityName}"`;
    case 'task_completed':
      return `completed task "${entityName}"`;
    case 'task_updated':
      return `updated task "${entityName}"`;
    case 'offer_created':
      return `submitted offer for "${entityName}"`;
    case 'offer_updated':
      return `updated offer for "${entityName}"`;
    case 'offer_status_changed':
      return `offer for "${entityName}" changed to ${details?.new_status || 'unknown'}`;
    case 'contact_created':
      return `added contact "${entityName}"`;
    case 'contact_updated':
      return `updated contact "${entityName}"`;
    case 'document_uploaded':
      return `uploaded document "${entityName}"`;
    case 'note_added':
      return `added note to "${entityName}"`;
    case 'financing_created':
      return `added financing for "${entityName}"`;
    case 'financing_updated':
      return `updated financing for "${entityName}"`;
    case 'om_uploaded':
      return `uploaded offering memorandum "${entityName}"`;
    case 'om_status_changed':
      return `changed OM status of "${entityName}" to ${details?.new_status || 'unknown'}`;
    case 'inbound_lead_received':
      return `new inbound lead received: "${entityName}"`;
    default:
      return `performed action on "${entityName}"`;
  }
}
